import { memo, useEffect, useState } from 'react';

import { cn } from '~/utils/cn';

const placeholderEmblem = '/assets/images/lily-emblem-placeholder.svg';

/**
 * CachedImage — two-layer in-memory cache using HTMLImageElement.
 *
 * Why HTMLImageElement instead of fetch() + Cache Storage:
 *   fetch() requires CORS headers on the image server. External CDN images
 *   load fine as <img> tags but fail fetch() with a CORS error. new Image()
 *   uses the same browser loading path as <img> — no CORS requirement — and
 *   keeping the HTMLImageElement reference prevents the browser from evicting
 *   decoded pixel data when the tab is backgrounded.
 *
 * Caching layers:
 *  1. imageCache  Map<url, HTMLImageElement> — keeps decoded pixel data alive.
 *  2. resolvedUrls Set<url> — sync flag so state initialises straight to
 *     'loaded' on remounts without a skeleton flash.
 */

const imageCache = new Map<string, HTMLImageElement>();
const resolvedUrls = new Set<string>();

type Status = 'loading' | 'loaded' | 'error';

function statusFor(src: string | null | undefined): Status {
  if (!src) return 'error';
  return resolvedUrls.has(src) ? 'loaded' : 'loading';
}

// Bundled with src so the component can detect prop changes without an effect.
interface State {
  src: string | null | undefined;
  status: Status;
}

interface Props {
  src: string | null | undefined;
  alt?: string;
  className?: string;
  /** Automation hook (default `cached-image`) on the loaded `<img>`; states use `-loading` / `-error`. */
  testId?: string;
}

function CachedImageBase({ src, alt = '', className, testId = 'cached-image' }: Props) {
  const [state, setState] = useState<State>(() => ({
    src,
    status: statusFor(src),
  }));

  // Derived-state pattern (https://react.dev/learn/you-might-not-need-an-effect
  // #adjusting-some-state-when-a-prop-changes): update synchronously during
  // render when src changes rather than inside an effect, so there is never a
  // committed frame with stale status and no synchronous setState-in-effect.
  if (state.src !== src) {
    setState({ src, status: statusFor(src) });
  }

  const { status } = state;

  useEffect(() => {
    if (!src || resolvedUrls.has(src)) return;

    let cancelled = false;

    let img = imageCache.get(src);
    if (!img) {
      img = new Image();
      imageCache.set(src, img);
      img.src = src;
    }

    const loadedImg = img;

    const handleLoad = () => {
      // Always populate the module-level cache, even if this component has
      // since unmounted — future mounts will hit resolvedUrls and skip the shimmer.
      resolvedUrls.add(src);
      if (cancelled) return;
      setState((prev) => (prev.src === src ? { src, status: 'loaded' } : prev));
    };
    const handleError = () => {
      if (cancelled) return;
      setState((prev) => (prev.src === src ? { src, status: 'error' } : prev));
    };

    // Image already finished loading before we attached listeners.
    if (loadedImg.complete) {
      // Defer via microtask — direct setState inside the effect body is
      // flagged by react-hooks/set-state-in-effect; a microtask keeps the
      // call outside the synchronous effect execution.
      void Promise.resolve().then(() => {
        if (loadedImg.naturalWidth > 0) handleLoad();
        else handleError();
      });
      return () => {
        cancelled = true;
      };
    }

    loadedImg.addEventListener('load', handleLoad);
    loadedImg.addEventListener('error', handleError);
    return () => {
      cancelled = true;
      loadedImg.removeEventListener('load', handleLoad);
      loadedImg.removeEventListener('error', handleError);
    };
  }, [src]);

  if (status === 'error') {
    return (
      <div
        aria-hidden="true"
        data-testid={`${testId}-error`}
        className={cn('bg-surface-progress-track flex items-center justify-center', className)}
      >
        <img src={placeholderEmblem} alt="" className="h-auto max-h-[40%] w-auto max-w-[40%]" />
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div
        className={cn('bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100', className)}
        style={{ backgroundSize: '200% 100%', animation: 'cachedImgShimmer 1.4s ease infinite' }}
        aria-busy="true"
        data-testid={`${testId}-loading`}
      />
    );
  }

  return (
    <img
      src={src ?? undefined}
      alt={alt}
      className={cn('object-cover', className)}
      decoding="async"
      data-testid={testId}
    />
  );
}

export const CachedImage = memo(CachedImageBase);
